import { computeArriveeEauCost } from './arriveeEauCalculations.js';

export const plomberieCost = (p) => {
  if (p === 'basique') return { low: 150, high: 250 };
  if (p === 'jardin') return { low: 400, high: 700 };
  return { low: 1200, high: 3000 };
};

// Coût par point d'eau raccordé, réseau intérieur affiné (apparent vs encastré) — utilisé
// uniquement quand plomberie==='interieur' et que la section est affinée.
export const POINT_EAU_COST = {
  apparent: { low: 100, high: 220 },
  encastre: { low: 150, high: 320 },
};

// Accessoires rattachés à la distribution/déclaration du volume, pas au traitement ni au pompage.
export const EXTRAS_COST = {
  niveau: { low: 50, high: 150, label: 'Indicateur de niveau' },
  compteur: { low: 100, high: 250, label: 'Compteur volumétrique' },
};

export const PLOMBERIE_LABELS = { basique: 'basique', jardin: 'réseau jardin', interieur: 'intégration intérieure' };
export const POSE_LABELS = { apparent: 'apparent', encastre: 'encastré' };

/** Nombre de points d'eau par défaut (WC + lave-linge actifs), dérivé des usages sans nouvel appel réseau. */
export const defaultNbPointsEau = (usages) => {
  const count = usages
    .filter(u => u.enabled && (u.category === 'wc' || u.category === 'washer'))
    .reduce((sum, u) => sum + (u.sourceCount ?? 1), 0);
  return count > 0 ? count : 1;
};

/**
 * @param {Object} plomberieState - { nbDescentes, distanceCanalisation, plomberie, poseType,
 *   nbPointsEau, niveau, compteur, refined }
 */
export const computePlomberieCost = (plomberieState) => {
  // 1. Arrivée d'eau (raccordement gouttière → cuve)
  const arrivee = computeArriveeEauCost({
    nbDescentes: plomberieState.nbDescentes,
    distance: plomberieState.distanceCanalisation,
    refined: plomberieState.refined,
  });

  // 2. Départ de l'eau (réseau de distribution)
  let depart;
  if (plomberieState.refined && plomberieState.plomberie === 'interieur') {
    const perPoint = POINT_EAU_COST[plomberieState.poseType] || POINT_EAU_COST.apparent;
    const n = Math.max(1, plomberieState.nbPointsEau || 1);
    depart = {
      breakdown: [{
        name: `Réseau intérieur (${n} point${n > 1 ? 's' : ''} d'eau, ${POSE_LABELS[plomberieState.poseType]})`,
        low: perPoint.low * n,
        high: perPoint.high * n,
      }],
      low: perPoint.low * n,
      high: perPoint.high * n,
    };
  } else {
    const plomb = plomberieCost(plomberieState.plomberie);
    depart = {
      breakdown: [{ name: `Plomberie (${PLOMBERIE_LABELS[plomberieState.plomberie]})`, low: plomb.low, high: plomb.high }],
      low: plomb.low,
      high: plomb.high,
    };
  }

  // 3. Accessoires
  const accessoires = [];
  let accessoiresLow = 0, accessoiresHigh = 0;
  Object.entries(EXTRAS_COST).forEach(([key, cost]) => {
    if (plomberieState[key]) {
      accessoires.push({ name: cost.label, low: cost.low, high: cost.high });
      accessoiresLow += cost.low;
      accessoiresHigh += cost.high;
    }
  });

  const breakdown = [...arrivee.breakdown, ...depart.breakdown, ...accessoires];

  return {
    breakdown,
    low: arrivee.low + depart.low + accessoiresLow,
    high: arrivee.high + depart.high + accessoiresHigh,
  };
};

/** Texte court pour l'en-tête replié de la section Plomberie. */
export const summarizePlomberie = (plomberieState) => {
  const n = Math.max(1, plomberieState.nbDescentes || 1);
  const departLabel = plomberieState.plomberie === 'interieur' && plomberieState.refined
    ? `intérieur ${Math.max(1, plomberieState.nbPointsEau || 1)} pt(s)`
    : PLOMBERIE_LABELS[plomberieState.plomberie];
  return `${n} descente${n > 1 ? 's' : ''} · ${departLabel}`;
};
